package songwritersnotebook.app.tutorial

import androidx.compose.ui.geometry.Rect
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import songwritersnotebook.app.data.*
import songwritersnotebook.app.database.SongRepository
import songwritersnotebook.app.utils.ChordUtils
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth

enum class TutorialStep {
    NONE,
    WELCOME,
    HOME_SETTINGS_BUTTON,
    SETTINGS_GENERAL,
    SETTINGS_ACCOUNT,
    SETTINGS_BACK,
    HOME_SEARCH,
    HOME_CREATE_SONG,
    HOME_SONG_CONTEXT,
    HOME_SHARED_TAB,
    HOME_SONG_CARD,
    HOME_TAP_SONG,
    EDITOR_INTRO,
    EDITOR_ADD_SECTION,
    EDITOR_WORD_FINDER_OPEN,
    EDITOR_WORD_FINDER_SEARCH,
    EDITOR_CHORD_LIBRARY_OPEN,
    EDITOR_CHORD_LIBRARY_SELECT,
    EDITOR_CHORD_LIBRARY_PROGRESSION_TAB,
    EDITOR_PROGRESSION_BUILDER,
    EDITOR_OPEN_CREATOR_EXPLAIN,
    EDITOR_CREATE_CHORD_VOICING,
    EDITOR_CREATE_CHORD_SHUFFLE,
    EDITOR_CREATE_CHORD_CUSTOM_TOGGLE,
    EDITOR_CREATE_CHORD_CUSTOM_EXPLAIN,
    EDITOR_CREATE_CHORD_TUNING,
    EDITOR_CREATE_CHORD_FAVORITE,
    EDITOR_CREATE_CHORD_SAVE,
    EDITOR_AI_OPEN,
    EDITOR_AI_HARMONY,
    EDITOR_CONTEXT_MENU_OPEN,
    EDITOR_CONTEXT_MENU_MOVE,
    EDITOR_CONTEXT_MENU_DUPLICATE_DELETE,
    EDITOR_CONTEXT_MENU_CHORDS,
    EDITOR_CONTEXT_MENU_ADD,
    EDITOR_SETTINGS_OPEN,
    EDITOR_STUDIO_OPEN,
    STUDIO_INTRO,
    STUDIO_SETTINGS,
    STUDIO_TABS,
    STUDIO_LENGTH,
    STUDIO_INTERVAL,
    STUDIO_DRAG,
    STUDIO_TRACK_SETTINGS,
    STUDIO_TRACK_MINIMIZE,
    STUDIO_CONTEXT_MENU,
    FINISH
}

data class TutorialStepInfo(
    val step: TutorialStep,
    val text: String,
    val onNext: () -> Unit = {},
    val requiresClickTarget: Boolean = false,
    val isLocal: Boolean = false
)

object TutorialManager {
    private val _currentStep = MutableStateFlow(TutorialStep.NONE)
    val currentStep: StateFlow<TutorialStep> = _currentStep.asStateFlow()

    private val _targetRect = MutableStateFlow<Rect?>(null)
    val targetRect: StateFlow<Rect?> = _targetRect.asStateFlow()

    private val _secondaryTargetRect = MutableStateFlow<Rect?>(null)
    val secondaryTargetRect: StateFlow<Rect?> = _secondaryTargetRect.asStateFlow()

    private val _tutorialText = MutableStateFlow("")
    val tutorialText: StateFlow<String> = _tutorialText.asStateFlow()

    private val _requiresClickTarget = MutableStateFlow(false)
    val requiresClickTarget: StateFlow<Boolean> = _requiresClickTarget.asStateFlow()

    private val _isCurrentStepLocal = MutableStateFlow(false)
    val isCurrentStepLocal: StateFlow<Boolean> = _isCurrentStepLocal.asStateFlow()

    private val _tutorialFinished = kotlinx.coroutines.flow.MutableSharedFlow<Unit>()
    val tutorialFinished: kotlinx.coroutines.flow.SharedFlow<Unit> = _tutorialFinished

    private val steps = mutableMapOf<TutorialStep, TutorialStepInfo>()
    private var stepOrder: List<TutorialStep> = emptyList()
    private val actions = mutableMapOf<TutorialStep, () -> Unit>()
    private val enterActions = mutableMapOf<TutorialStep, () -> Unit>()

    const val TUTORIAL_SONG_ID = "tutorial-song"
    private var repository: SongRepository? = null

    fun registerAction(step: TutorialStep, action: () -> Unit) {
        actions[step] = action
    }

    fun registerEnterAction(step: TutorialStep, action: () -> Unit) {
        enterActions[step] = action
    }

    fun triggerNext() {
        val currentAction = actions[_currentStep.value]
        nextStep()
        currentAction?.invoke()
    }

    fun startTutorial(songRepository: SongRepository?) {
        repository = songRepository
        if (songRepository != null) {
            CoroutineScope(songwritersnotebook.app.AppDispatchers.IO).launch {
                songRepository.saveSong(createTutorialSong())
            }
        }

        loadScript(getDefaultScript())
        if (stepOrder.isNotEmpty()) {
            setStep(stepOrder.first())
        }
    }

    fun stopTutorial() {
        if (_currentStep.value == TutorialStep.NONE) return
        
        _currentStep.value = TutorialStep.NONE
        _targetRect.value = null
        _secondaryTargetRect.value = null
        cleanUp()
        CoroutineScope(Dispatchers.Main).launch {
            _tutorialFinished.emit(Unit)
        }
    }

    fun cleanUp() {
        repository?.let { repo ->
            CoroutineScope(songwritersnotebook.app.AppDispatchers.IO).launch {
                repo.deleteSong(TUTORIAL_SONG_ID)
            }
        }
    }

    fun nextStep() {
        val currentIndex = stepOrder.indexOf(_currentStep.value)
        if (currentIndex != -1 && currentIndex < stepOrder.size - 1) {
            setStep(stepOrder[currentIndex + 1])
        } else {
            stopTutorial()
        }
    }

    fun previousStep() {
        val currentIndex = stepOrder.indexOf(_currentStep.value)
        if (currentIndex > 0) {
            val prevIndex = currentIndex - 1
            setStep(stepOrder[prevIndex])
        }
    }

    fun getCurrentStepIndex(): Int {
        return stepOrder.indexOf(_currentStep.value)
    }

    fun getTotalSteps(): Int {
        return stepOrder.size
    }

    private fun setStep(step: TutorialStep) {
        _currentStep.value = step
        val info = steps[step]
        _tutorialText.value = info?.text ?: ""
        _requiresClickTarget.value = info?.requiresClickTarget ?: false
        _isCurrentStepLocal.value = info?.isLocal ?: false
        _targetRect.value = null
        _secondaryTargetRect.value = null
        
        enterActions[step]?.invoke()
    }

    fun updateTargetRect(step: TutorialStep, rect: Rect, isSecondary: Boolean = false) {
        // Rationale: Web Skiko crashes if NaN values are rounded during rendering of highlights
        if (rect.left.isNaN() || rect.top.isNaN() || rect.right.isNaN() || rect.bottom.isNaN() ||
            rect.left.isInfinite() || rect.top.isInfinite() || rect.right.isInfinite() || rect.bottom.isInfinite()) {
            return
        }

        if (_currentStep.value == step) {
            if (isSecondary) {
                _secondaryTargetRect.value = rect
            } else {
                _targetRect.value = rect
            }
        }
    }

    fun loadScript(newSteps: List<TutorialStepInfo>) {
        steps.clear()
        newSteps.forEach { steps[it.step] = it }
        stepOrder = newSteps.map { it.step }
    }

    private fun createTutorialSong(): Song {
        return Song(
            id = TUTORIAL_SONG_ID,
            title = "Tutorial Song",
            ownerId = Firebase.auth.currentUser?.uid ?: "",
            lastModified = Clock.System.now(),
            sections = listOf(
                Section(
                    title = "Verse 1",
                    lines = listOf(
                        Line(text = "The [C]road goes on and [G]on,"),
                        Line(text = "Beneath the [Am]pale blue [F]sky."),
                        Line(text = "I'm just a [C]wandering [G]soul,"),
                        Line(text = "Who never [F]questioned [C]why.")
                    )
                ),
                Section(
                    title = "Chorus",
                    lines = listOf(
                        Line(text = "Oh, [F]sing me a [C]song of the [G]open [Am]road,"),
                        Line(text = "A [F]melody [C]wild and [G]free."),
                        Line(text = "Carry my [F]burdens, [C]lighten my [G]load,"),
                        Line(text = "And [F]bring my [G]spirit back to [C]me.")
                    )
                )
            ),
            recordings = emptyMap(),
            guitarTuning = ChordUtils.DEFAULT_GUITAR_TUNING,
            guitarCapo = 0,
            chordDefinitions = mapOf(
                "C" to ChordDefinition(name = "C", root = "C", quality = "maj", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["C"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["C"]?.map { it.toString() }),
                "G" to ChordDefinition(name = "G", root = "G", quality = "maj", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["G"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["G"]?.map { it.toString() }),
                "Am" to ChordDefinition(name = "Am", root = "A", quality = "m", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["Am"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["Am"]?.map { it.toString() }),
                "F" to ChordDefinition(name = "F", root = "F", quality = "maj", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["F"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["F"]?.map { it.toString() }),
                "Dm" to ChordDefinition(name = "Dm", root = "D", quality = "m", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["Dm"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["Dm"]?.map { it.toString() }),
                "E7" to ChordDefinition(name = "E7", root = "E", quality = "7", pianoNotes = ChordUtils.PIANO_CHORD_VOICINGS["E7"], guitarTab = ChordUtils.GUITAR_CHORD_TABS["E7"]?.map { it.toString() })
            ),
            bpm = 120,
            studioState = StudioState(
                tracks = listOf(
                    Track(
                        id = "tutorial-lyrics-track",
                        name = "Lyrics",
                        items = emptyList()
                    ),
                    Track(
                        id = "tutorial-vocal-track",
                        name = "Vocals",
                        items = emptyList()
                    ),
                    Track(
                        id = "tutorial-guitar-track",
                        name = "Guitar",
                        items = emptyList()
                    ),
                    Track(
                        id = "tutorial-piano-track",
                        name = "Piano",
                        items = listOf(
                            TrackItem(
                                id = "tutorial-c-chord",
                                beat = 0f,
                                duration = 4f,
                                type = "chord",
                                data = TrackData(
                                    name = "C",
                                    instrument = "piano"
                                )
                            )
                        )
                    ),
                    Track(
                        id = "tutorial-bass-track",
                        name = "Bass",
                        items = emptyList()
                    ),
                    Track(
                        id = "tutorial-drums-track",
                        name = "Drums",
                        items = emptyList()
                    )
                )
            )
        )
    }

    private fun getDefaultScript(): List<TutorialStepInfo> {
        return listOf(
            TutorialStepInfo(TutorialStep.WELCOME, "Welcome to Songwriter's Notebook!"),
            TutorialStepInfo(TutorialStep.HOME_SETTINGS_BUTTON, "Let's start our tutorial with the app Settings", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.SETTINGS_GENERAL, "Here you can manage global settings..."),
            TutorialStepInfo(TutorialStep.SETTINGS_ACCOUNT, "Most importantly, sign in to manage your subscription and account data!"),
            TutorialStepInfo(TutorialStep.SETTINGS_BACK, "Let's return to the Home screen.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.HOME_SEARCH, "Here we can Search by title or lyrics..."),
            TutorialStepInfo(TutorialStep.HOME_CREATE_SONG, "Add a new song..."),
            TutorialStepInfo(TutorialStep.HOME_SONG_CONTEXT, "Or export our song as text or audio, or delete a song via the context menu."),
            TutorialStepInfo(TutorialStep.HOME_SHARED_TAB, "Songs shared with you appear as invites in the shared tab.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.HOME_SONG_CARD, "Let's tap a song to open the Lyric Editor.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.HOME_TAP_SONG, "Let's tap a song to navigate to the Lyric Editor.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_INTRO, "The Lyric Editor displays lyrics, sections, chords, rhymes, syllables, and guitar tabs. Treat it like you would a normal text editor."),
            TutorialStepInfo(TutorialStep.EDITOR_ADD_SECTION, "Add a new section with the tap of a button..."),
            TutorialStepInfo(TutorialStep.EDITOR_WORD_FINDER_OPEN, "Use the Word Finder to lookup rhymes and synonyms...", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_WORD_FINDER_SEARCH, "Simply search for a word and browse the results.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CHORD_LIBRARY_OPEN, "Open the Chord Library to manage the chords associated with your song...", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_CHORD_LIBRARY_SELECT, "Here chords can be created and edited for guitar and piano. Selecting a chord and tapping the lyrics places the chord into the selected position within the song."),
            TutorialStepInfo(TutorialStep.EDITOR_CHORD_LIBRARY_PROGRESSION_TAB, "You can also switch to the Progressions tab to manage chord progressions.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_PROGRESSION_BUILDER, "Click the Add button to open the Progression Builder to construct a progression for your song!"),
            TutorialStepInfo(TutorialStep.EDITOR_OPEN_CREATOR_EXPLAIN, "Click add or edit to open the chord creator."),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_VOICING, "Select a preset chord and quality to hear its voicing played on the selected instrument.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_SHUFFLE, "Shuffle to select a random preset chord/quality.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_TOGGLE, "Select 'Custom' or edit the fretboard/piano to create a custom chord.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_EXPLAIN, "Custom chords have separate guitar/piano voicings, so they must be edited independently.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_TUNING, "Guitar tuning and capo adjustments are supported. Capo fingerings auto-adjust for preset chords in standard tuning.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_FAVORITE, "For quick chord access across songs, add a chord to your favorites list.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CREATE_CHORD_SAVE, "Once finished, name your chord and save it to add it to your Chord Library and song lyrics.", isLocal = true),
    TutorialStepInfo(TutorialStep.EDITOR_AI_OPEN, "Tap the sparkle icon for AI help, or hold it to speak directly with Harmony, your AI songwriting assistant.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_AI_HARMONY, "The Harmony tab allows you to interact with your AI assistant via voice or text. Harmony can suggest chords or lyrics, identify chord names by description, and even perform actions on your song such as adding sections or changing the BPM for you.", isLocal = true),
            TutorialStepInfo(TutorialStep.EDITOR_CONTEXT_MENU_OPEN, "Tap a line or section's context menu for further refinement options and tools.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.EDITOR_CONTEXT_MENU_MOVE, "Move a line or section up or down."),
            TutorialStepInfo(TutorialStep.EDITOR_CONTEXT_MENU_DUPLICATE_DELETE, "Duplicate or delete a line or section."),
            TutorialStepInfo(TutorialStep.EDITOR_CONTEXT_MENU_CHORDS, "Select a chord to refine it's placement within the line."),
            TutorialStepInfo(TutorialStep.EDITOR_CONTEXT_MENU_ADD, "Add a recording or guitar tab to the section or line."),
            TutorialStepInfo(TutorialStep.EDITOR_SETTINGS_OPEN, "The Editor settings can be used to manage the song BPM and the visibility of various items."),
            TutorialStepInfo(TutorialStep.EDITOR_STUDIO_OPEN, "Next, arrange your song in the Studio!", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.STUDIO_INTRO, "The Studio allows for the arrangement of all song Chords, lyrics, and media on a track with other instruments."),
            TutorialStepInfo(TutorialStep.STUDIO_SETTINGS, "Studio settings control the BPM, metronome, and snapping precision for track items."),
            TutorialStepInfo(TutorialStep.STUDIO_TABS, "Select lyrics, recordings, chords, or notes to add to the tracks.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.STUDIO_LENGTH, "When setting a track item, the length determines the length in beats for the selected note/chord."),
            TutorialStepInfo(TutorialStep.STUDIO_INTERVAL, "The interval determines how many beats the playhead will advance once the item has been set."),
            TutorialStepInfo(TutorialStep.STUDIO_DRAG, "Track items can be lengthened, shortened, or moved by dragging the handles or center of the item respectively."),
            TutorialStepInfo(TutorialStep.STUDIO_TRACK_SETTINGS, "Track settings control the volume, mute/solo options, and strum speed for chords.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.STUDIO_TRACK_MINIMIZE, "Tracks can be minimized for easier navigation and less visual clutter."),
            TutorialStepInfo(TutorialStep.STUDIO_CONTEXT_MENU, "Selected track items can be nudged, duplicated or deleted, and lasso select is also available.", requiresClickTarget = true),
            TutorialStepInfo(TutorialStep.FINISH, "That's it, have fun! This tutorial can be launched at any time from the main App Settings menu.")
        )
    }
}

